import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CookiePolicyModal from './CookiePolicyModal';

const COOKIE_CONSENT_KEY = 'bf_cookie_consent';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({ ...prefs, timestamp: new Date().toISOString() })
    );
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const all = { necessary: true, analytics: true, marketing: true };
    setPreferences(all);
    saveConsent(all);
  };

  const handleRejectAll = () => {
    const minimal = { necessary: true, analytics: false, marketing: false };
    setPreferences(minimal);
    saveConsent(minimal);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePref = (key: keyof Omit<CookiePreferences, 'necessary'>) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 md:pb-6"
          >
            <div className="max-w-4xl mx-auto bg-dark text-white rounded-2xl overflow-hidden">
              {/* Main Banner */}
              <div className="px-6 py-5 md:px-8 md:py-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
                  {/* Icon + Text */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-2xl text-primary"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-base mb-1">Utilizziamo i cookie</h3>
                      <p className="text-white/70 text-xs leading-relaxed">
                        Questo sito utilizza cookie tecnici necessari al funzionamento e, previo consenso, cookie analitici e di marketing per migliorare la tua esperienza.{' '}
                        <button
                          type="button"
                          onClick={() => setIsPolicyOpen(true)}
                          className="text-primary underline hover:text-primary/80 transition-colors cursor-pointer"
                        >
                          Leggi la Cookie Policy
                        </button>
                      </p>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowDetails(prev => !prev)}
                      className="px-4 py-2.5 rounded-full border border-white/30 text-white/80 text-xs font-semibold hover:border-white/60 hover:text-white transition-all whitespace-nowrap cursor-pointer"
                    >
                      {showDetails ? 'Nascondi dettagli' : 'Personalizza'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRejectAll}
                      className="px-4 py-2.5 rounded-full border border-white/30 text-white/80 text-xs font-semibold hover:border-white/60 hover:text-white transition-all whitespace-nowrap cursor-pointer"
                    >
                      Rifiuta tutti
                    </button>
                    <button
                      type="button"
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 rounded-full bg-primary text-white text-xs font-bold hover:bg-primary/90 transition-all whitespace-nowrap cursor-pointer"
                    >
                      Accetta tutti
                    </button>
                  </div>
                </div>

                {/* Detailed Preferences */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 pt-5 border-t border-white/10 space-y-3">
                        {/* Necessary */}
                        <div className="flex items-center justify-between gap-4 bg-white/5 rounded-xl px-4 py-3">
                          <div>
                            <div className="text-sm font-semibold flex items-center gap-2">
                              Cookie Tecnici
                              <span className="text-xs font-normal text-white/50 bg-white/10 px-2 py-0.5 rounded-full">Sempre attivi</span>
                            </div>
                            <p className="text-xs text-white/50 mt-0.5">Necessari per il funzionamento del sito. Non possono essere disabilitati.</p>
                          </div>
                          <div className="w-10 h-6 flex items-center justify-center flex-shrink-0">
                            <div className="w-10 h-6 bg-primary rounded-full relative flex-shrink-0">
                              <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                            </div>
                          </div>
                        </div>

                        {/* Analytics */}
                        <div className="flex items-center justify-between gap-4 bg-white/5 rounded-xl px-4 py-3">
                          <div>
                            <div className="text-sm font-semibold">Cookie Analitici</div>
                            <p className="text-xs text-white/50 mt-0.5">Ci aiutano a capire come gli utenti interagiscono con il sito (es. Google Analytics).</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePref('analytics')}
                            className={`w-10 h-6 rounded-full relative flex-shrink-0 transition-colors cursor-pointer ${preferences.analytics ? 'bg-primary' : 'bg-white/20'}`}
                            aria-label="Toggle cookie analitici"
                          >
                            <motion.div
                              animate={{ x: preferences.analytics ? 16 : 2 }}
                              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                              className="absolute top-1 w-4 h-4 bg-white rounded-full"
                            />
                          </button>
                        </div>

                        {/* Marketing */}
                        <div className="flex items-center justify-between gap-4 bg-white/5 rounded-xl px-4 py-3">
                          <div>
                            <div className="text-sm font-semibold">Cookie di Marketing</div>
                            <p className="text-xs text-white/50 mt-0.5">Utilizzati per mostrare annunci pertinenti (es. Facebook Pixel, LinkedIn).</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePref('marketing')}
                            className={`w-10 h-6 rounded-full relative flex-shrink-0 transition-colors cursor-pointer ${preferences.marketing ? 'bg-primary' : 'bg-white/20'}`}
                            aria-label="Toggle cookie marketing"
                          >
                            <motion.div
                              animate={{ x: preferences.marketing ? 16 : 2 }}
                              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                              className="absolute top-1 w-4 h-4 bg-white rounded-full"
                            />
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={handleSavePreferences}
                          className="w-full mt-2 py-3 rounded-full bg-white text-dark text-xs font-bold hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
                        >
                          Salva preferenze
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CookiePolicyModal isOpen={isPolicyOpen} onClose={() => setIsPolicyOpen(false)} />
    </>
  );
}
